import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { TLoginFormFields } from '../../pages/auth/login';
import { TRegistrationFormFields } from '../../pages/auth/registration';
import Rest from '../../services/rest';
import useGlobalLoader from '../app/useGlobalLoader';
import useMenu from '../menu/useMenu';
import useRest from '../useRest';

const useAuth = () => {
    const gl = useGlobalLoader();
    const rest = useRest();

    const menu = useMenu(true, false);

    const navigate = useNavigate();

    const checkAuth = async () => {
        gl.toggleLoader({
            loading: true,
            title: 'Инициализация',
        });

        const result = await menu.getMenuData();
        navigate(result.isError ? '/login' : (result.response?.data?.homeUrl || '/ticket/list?type=actual'));

        gl.toggleLoader({
            loading: false,
            title: 'Инициализация',
        });
    };

    const onLoginSucceed = (token: string) => {
        Rest.saveToken(token);
        navigate('/');
    };

    const login = async (email: string, password: string): Promise<Record<keyof TLoginFormFields, string>|undefined> => {
        gl.toggleLoader({
            loading: true,
            title: 'Авторизация',
        });
        const result = await rest.fetch({
            objectName: '/user/login',
            method: 'post',
            data: {
                password,
                username: email,
            },
        });
        if (!result.isError && result.response && typeof result.response === 'object') {
            onLoginSucceed(result.response.data.token);
        }
        if (result.isError) {
            Swal.fire({
                toast: true,
                title: 'Ошибка авторизации',
                text: result.response?.data?.errorText || result.err || 'Непредвиденная ошибка',
                icon: 'error',
                position: 'top-right',
                showConfirmButton: false,
                timer: 3000,
            });
        }
        gl.toggleLoader({
            loading: false,
            title: 'Авторизация',
        });
        return result.response?.data?.errorFields;
    };

    const register = async (fields: TRegistrationFormFields): Promise<Record<(keyof TRegistrationFormFields), string>|undefined> => {
        const keys = Object.keys(fields) as (keyof TRegistrationFormFields)[];
        const data = keys.reduce((prev, key) => {
            const value = fields[key]?.value;
            return value ? Object.assign(prev, { [key]: value }) : prev;
        }, {} as Record<keyof TRegistrationFormFields, any>);

        gl.toggleLoader({
            loading: true,
            title: 'Отправка данных',
        });

        const result = await rest.fetch({
            method: 'post',
            objectName: '/registration/company',
            data,
        });

        if (result.response?.data && !result.response.data.error) {
            if (typeof data.invite === 'string') {
                onLoginSucceed(result.response.data.token);
            } else {
                navigate('/info', {
                    state: {
                        title: result.response.data.errorPageHeading || 'Поздравляем!',
                        text: result.response.data.errorPageText || 'Вы успешно зарегистрировались. Для активации учетной записи зайдите в письмо, отправленное на указаную почту.',
                    },
                });
            }
        } else if (typeof result.response?.data?.errorFields === 'undefined') {
            Swal.fire({
                title: result.response?.data?.errorPageHeading || 'Произошла ошибка',
                text: result.response?.data?.errorPageText || 'Непредвиденная ошибка.',
                icon: 'error',
            });
        }

        gl.toggleLoader({
            loading: false,
            title: 'Отправка данных',
        });

        return result.response?.data?.errorFields;
    };

    const registrationActivate = async () => {
        gl.toggleLoader({
            loading: true,
            title: 'Активация пользователя',
        });

        const { search } = new URL(window.location.href);

        const result = await rest.fetch({
            method: 'get',
            objectName: `/registration/activation${search}`,
        });

        gl.toggleLoader({
            loading: false,
            title: 'Активация пользователя',
        });

        if (result.response?.data?.errorPageRedirect) {
            navigate('/info', {
                state: {
                    title: result.response?.data?.errorPageHeading || 'Произошла ошибка',
                    text: result.response?.data?.errorPageText || 'Непредвиденная ошибка.',
                },
            });
        } else if (typeof result.response?.data?.token === 'string') {
            onLoginSucceed(result.response.data.token);
        } else {
            const { isConfirmed } = await Swal.fire({
                title: result.response?.data?.errorPageHeading || 'Произошла ошибка',
                text: result.response?.data?.errorPageText || 'Непредвиденная ошибка.',
                icon: 'error',
                allowOutsideClick: false,
                allowEscapeKey: false,
            });

            if (isConfirmed) {
                navigate('/login');
            }
        }
    };

    return {
        checkAuth,
        login,
        removeToken: Rest.removeToken,
        register,
        registrationActivate,
    };
};

export default useAuth;
